import { type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno } from '../lib/format';
import styles from './screens.module.css';

interface Lancamento {
  id: number;
  data: string;
  descricao: string;
  valor: number;
  tipo: string;
  categoria: string | null;
  conciliacao: string;
  unidadeId: number | null;
  demonstrativoUnidadeId: number | null;
}
export interface Fechamento {
  /** `null` = saldo final não informado no envio do extrato. */
  ok: boolean | null;
  saldoCalculado: number;
  saldoDeclarado: number | null;
  diferenca: number | null;
  pendentes: number;
}
type StatusFatura = 'pago' | 'divergente' | 'atrasado' | 'aberto';
interface Fatura {
  id: number;
  unidadeNumero: string | null;
  subtotal: number;
  vencimento: string | null;
  status: StatusFatura;
}
interface ResultadoConciliacao {
  error?: string;
  situacao?: string;
  diasAtraso?: number;
  multa?: number;
  juros?: number;
}
type Faturas = Fatura[] | 'erro' | 'sem_competencia' | null;
type Aviso = { kind: 'success' | 'warning' | 'danger'; texto: string };

const CATEGORIAS = [
  '',
  'receita_condominio',
  'despesa_ordinaria',
  'despesa_extraordinaria',
  'outros',
];
const STATUS: Array<[string, string]> = [
  ['pendente', '⚠️ pendente'],
  ['confirmado', '✅ confirmado'],
  ['ausente', '❌ ausente'],
];
const STATUS_FATURA: Record<StatusFatura, string> = {
  pago: 'Pago',
  divergente: 'Divergente',
  atrasado: 'Atrasado',
  aberto: 'Em aberto',
};
const MSG_FATURA = 'Escolha a fatura que este lançamento pagou.';
/** Erros da API de conciliação (código → mensagem). */
const ERRO_CONCILIAR: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  lancamento_ja_conciliado: 'Este lançamento já está conciliado com uma fatura.',
  lancamento_nao_e_entrada: 'Só crédito paga fatura.',
  lancamento_not_found: 'Lançamento não encontrado.',
  demonstrativo_not_found: 'Fatura não encontrada.',
  fatura_ja_paga: 'Esta fatura já tem pagamento registrado.',
};
const ERRO_CONCILIAR_PADRAO = 'Não foi possível conciliar. Tente novamente.';
/** Erros que indicam dado já mudado no servidor (outra aba): a tela fecha o formulário e relê. */
const ERRO_CONCILIAR_DADO_VELHO: Record<string, true> = {
  lancamento_ja_conciliado: true,
  fatura_ja_paga: true,
  lancamento_not_found: true,
  demonstrativo_not_found: true,
};

/** Aviso do resultado da conciliação, com a situação, o atraso e os encargos do servidor. */
function resumoConciliacao(numero: string, r: ResultadoConciliacao): Aviso {
  if (r.situacao === 'divergente') {
    return {
      kind: 'warning',
      texto: `Valor diferente do cobrado: a fatura da unidade ${numero} ficou como divergente e continua em "Quem falta pagar".`,
    };
  }
  const dias = r.diasAtraso ?? 0;
  if (r.situacao === 'atrasado' && dias > 0) {
    return {
      kind: 'warning',
      texto: `Lançamento conciliado com a unidade ${numero}: fatura paga com ${dias} ${dias === 1 ? 'dia' : 'dias'} de atraso (multa de ${brl(r.multa ?? 0)} e juros de ${brl(r.juros ?? 0)}, cobrados no mês seguinte).`,
    };
  }
  return { kind: 'success', texto: `Lançamento conciliado com a unidade ${numero}: fatura paga.` };
}

/**
 * Conferência de um lote do extrato (RF-A09, RF-A10, RF-A11, RF-D02): fechamento do saldo,
 * quem falta pagar na competência e os lançamentos, com a conciliação de crédito ↔ fatura.
 * `editavel` só para o síndico de condomínio ativo (conciliar e o PATCH são
 * `requireRole('sindico')`); o contábil e o tenant suspenso só leem. Saldos, situação,
 * atraso e encargos vêm sempre do servidor.
 */
export function ConferenciaLote({
  lote,
  editavel,
  onAlterado,
}: {
  lote: {
    id: number;
    competenciaId: number | null;
    competencia: { ano: number; mes: number } | null;
  };
  editavel: boolean;
  /** Algo mudou no lote (conciliação): a lista de lotes pode ter outra situação. */
  onAlterado: () => void;
}) {
  const [lancs, setLancs] = useState<Lancamento[] | null>(null);
  const [fechamento, setFechamento] = useState<Fechamento | 'erro' | null>(null);
  const [faturas, setFaturas] = useState<Faturas>(null);
  const [conciliandoId, setConciliandoId] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  // O componente é remontado a cada lote: respostas de um lote já fechado são descartadas.
  const vivo = useRef(true);

  const carregar = useCallback(async () => {
    const [l, f, fs] = await Promise.all([
      getJson<{ lancamentos: Lancamento[] }>(
        `/api/extratos/${encodeURIComponent(lote.id)}/lancamentos`,
      ),
      getJson<Fechamento>(`/api/extratos/${encodeURIComponent(lote.id)}/fechamento`),
      lote.competenciaId === null
        ? Promise.resolve(null)
        : getJson<{ demonstrativos: Fatura[] }>(
            `/api/competencias/${encodeURIComponent(lote.competenciaId)}/demonstrativos`,
          ),
    ]);
    if (!vivo.current) return;
    setLancs(l?.lancamentos ?? []);
    setFechamento(f ?? 'erro');
    if (lote.competenciaId === null) setFaturas('sem_competencia');
    else setFaturas(fs ? fs.demonstrativos : 'erro');
  }, [lote.id, lote.competenciaId]);

  useEffect(() => {
    vivo.current = true;
    void carregar();
    return () => {
      vivo.current = false;
    };
  }, [carregar]);

  async function aoFalharConciliar(texto: string, dadoVelho: boolean) {
    setAviso({ kind: 'danger', texto });
    if (!dadoVelho) return;
    setConciliandoId(null);
    await carregar();
    onAlterado();
  }

  async function aoConciliar(numero: string, r: ResultadoConciliacao) {
    setConciliandoId(null);
    setAviso(resumoConciliacao(numero, r));
    await carregar();
    onAlterado();
  }

  const lista = Array.isArray(faturas) ? faturas : [];
  const pendentes = lista.filter((f) => f.status !== 'pago');
  // Só fatura sem pagamento recebe crédito: a divergente já recebeu um e fica só em "quem falta".
  const conciliaveis = lista.filter((f) => f.status === 'aberto' || f.status === 'atrasado');
  const conciliando = lancs?.find((l) => l.id === conciliandoId) ?? null;
  const fatura = (id: number | null) => lista.find((f) => f.id === id) ?? null;

  return (
    <div className={styles.stack}>
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      <div data-testid="fechamento">
        <Card title="Fechamento do saldo">
          {fechamento === null ? (
            <Spinner />
          ) : fechamento === 'erro' ? (
            <Alert kind="danger">Não foi possível carregar o fechamento do saldo.</Alert>
          ) : (
            <PainelFechamento f={fechamento} />
          )}
        </Card>
      </div>

      {faturas === 'sem_competencia' ? (
        <Alert kind="info">Este lote não tem competência: não dá para conciliar com fatura.</Alert>
      ) : lote.competencia && faturas !== null ? (
        <div data-testid="quem-falta">
          <Card title={`Quem falta pagar em ${mesAno(lote.competencia)}`}>
            {faturas === 'erro' ? (
              <Alert kind="danger">Não foi possível carregar as faturas da competência.</Alert>
            ) : lista.length === 0 ? (
              <EmptyState>Faturas ainda não geradas nesta competência.</EmptyState>
            ) : pendentes.length === 0 ? (
              <EmptyState>Todas as faturas desta competência estão pagas.</EmptyState>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Unidade</th>
                      <th>Vencimento</th>
                      <th className={styles.num}>Valor</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendentes.map((f) => (
                      <tr key={f.id}>
                        <th scope="row">{f.unidadeNumero ?? '—'}</th>
                        <td>{f.vencimento ? dataBr(f.vencimento) : '—'}</td>
                        <td className={styles.num}>{brl(f.subtotal)}</td>
                        <td>
                          <Badge tone={f.status === 'aberto' ? undefined : 'warning'}>
                            {STATUS_FATURA[f.status]}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      ) : null}

      {editavel && conciliando ? (
        <ConciliarForm
          key={conciliando.id}
          lancamento={conciliando}
          faturas={conciliaveis}
          ocupado={ocupado}
          setOcupado={setOcupado}
          onConciliado={aoConciliar}
          onCancelar={() => setConciliandoId(null)}
          onErro={aoFalharConciliar}
        />
      ) : null}

      <Card title={`Lançamentos do lote #${lote.id}`}>
        {lancs === null ? (
          <Spinner />
        ) : lancs.length === 0 ? (
          <EmptyState>Nenhum lançamento neste lote.</EmptyState>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th className={styles.num}>Valor</th>
                  <th>Tipo</th>
                  <th>Categoria</th>
                  <th>Conciliação</th>
                  <th>Fatura</th>
                  {editavel ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {lancs.map((l) => {
                  const conciliada = fatura(l.demonstrativoUnidadeId);
                  return (
                    <LinhaLancamento
                      key={`${l.id}-${l.demonstrativoUnidadeId ?? ''}`}
                      lanc={l}
                      editavel={editavel}
                      onSalvo={() => {
                        void carregar();
                        onAlterado();
                      }}
                      faturaCelula={
                        l.demonstrativoUnidadeId !== null ? (
                          `Unidade ${conciliada?.unidadeNumero ?? `#${l.unidadeId ?? '?'}`}`
                        ) : editavel && l.tipo === 'entrada' && Array.isArray(faturas) ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Conciliar lançamento ${l.id} com uma fatura`}
                            onClick={() => {
                              setAviso(null);
                              setConciliandoId(l.id);
                            }}
                          >
                            Conciliar
                          </Button>
                        ) : (
                          '—'
                        )
                      }
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Saldo calculado pelo extrato × saldo declarado, com a divergência em destaque. */
export function PainelFechamento({ f }: { f: Fechamento }) {
  return (
    <>
      <p>Saldo calculado pelo extrato: {brl(f.saldoCalculado)}</p>
      {f.saldoDeclarado !== null ? <p>Saldo declarado: {brl(f.saldoDeclarado)}</p> : null}
      {f.ok === true && f.pendentes === 0 ? <Badge tone="success">Saldo confere</Badge> : null}
      {f.ok === false && f.diferenca !== null ? (
        <Alert kind="warning">
          Divergência de {brl(Math.abs(f.diferenca))}: o saldo calculado pelo extrato é{' '}
          {f.diferenca > 0 ? 'maior' : 'menor'} que o declarado.
        </Alert>
      ) : null}
      {f.ok === null ? (
        <p>Saldo final não informado no envio do extrato: não há como conferir.</p>
      ) : null}
      {f.pendentes > 0 ? (
        <p>
          {f.pendentes} {f.pendentes === 1 ? 'lançamento pendente' : 'lançamentos pendentes'} de
          conciliação.
        </p>
      ) : null}
    </>
  );
}

/** Liga um crédito a uma fatura em aberto; situação, atraso e encargos vêm do servidor. */
function ConciliarForm({
  lancamento,
  faturas,
  ocupado,
  setOcupado,
  onConciliado,
  onCancelar,
  onErro,
}: {
  lancamento: Lancamento;
  faturas: Fatura[];
  ocupado: boolean;
  setOcupado: (v: boolean) => void;
  onConciliado: (numero: string, r: ResultadoConciliacao) => Promise<void>;
  onCancelar: () => void;
  onErro: (texto: string, dadoVelho: boolean) => void;
}) {
  const [escolhida, setEscolhida] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (ocupado) return;
    const alvo = faturas.find((f) => String(f.id) === escolhida);
    if (!alvo) {
      setErro(MSG_FATURA);
      return;
    }
    setErro(null);
    setOcupado(true);
    const res = await mutateJson<ResultadoConciliacao>(
      `/api/lancamentos/${encodeURIComponent(lancamento.id)}/conciliar`,
      { demonstrativoUnidadeId: alvo.id },
    );
    setOcupado(false);
    if (!res.ok) {
      const codigo = res.data?.error;
      onErro(
        (codigo && ERRO_CONCILIAR[codigo]) || ERRO_CONCILIAR_PADRAO,
        !!codigo && Object.hasOwn(ERRO_CONCILIAR_DADO_VELHO, codigo),
      );
      return;
    }
    await onConciliado(alvo.unidadeNumero ?? '', res.data ?? {});
  }

  return (
    <Card
      title={`Conciliar o lançamento #${lancamento.id} (${brl(lancamento.valor)} em ${dataBr(lancamento.data)})`}
    >
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Fatura" error={erro}>
            {(id) => (
              <Select id={id} value={escolhida} onChange={(e) => setEscolhida(e.target.value)}>
                <option value="">Escolha a fatura</option>
                {faturas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.unidadeNumero ?? '—'} — {brl(f.subtotal)}
                    {f.vencimento ? ` · vence ${dataBr(f.vencimento)}` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={ocupado}>
            Confirmar conciliação
          </Button>
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

function LinhaLancamento({
  lanc,
  editavel,
  onSalvo,
  faturaCelula,
}: {
  lanc: Lancamento;
  editavel: boolean;
  /** Conciliação salva: a situação do lote na lista pode ter mudado. */
  onSalvo: () => void;
  /** Conteúdo da coluna "Fatura": unidade conciliada, botão de conciliar ou traço. */
  faturaCelula: ReactNode;
}) {
  const [categoria, setCategoria] = useState(lanc.categoria ?? '');
  const [conciliacao, setConciliacao] = useState(lanc.conciliacao);
  const [label, setLabel] = useState('Salvar');

  async function salvar() {
    const res = await mutateJson(
      `/api/lancamentos/${encodeURIComponent(lanc.id)}`,
      { categoria: categoria || null, conciliacao },
      'PATCH',
    );
    setLabel(res.ok ? '✓ salvo' : '⚠ erro');
    if (res.ok) onSalvo();
    setTimeout(() => setLabel('Salvar'), 2000);
  }

  const statusLabel = STATUS.find(([v]) => v === lanc.conciliacao)?.[1] ?? lanc.conciliacao;
  return (
    <tr>
      <td>{lanc.data}</td>
      <td>{lanc.descricao}</td>
      <td className={styles.num}>{brl(lanc.valor)}</td>
      <td>{lanc.tipo === 'entrada' ? '↑ entrada' : '↓ saída'}</td>
      {editavel ? (
        <>
          <td>
            <Select
              inline
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              aria-label="Categoria"
            >
              {CATEGORIAS.map((c) => (
                <option key={c} value={c}>
                  {c || '(sem categoria)'}
                </option>
              ))}
            </Select>
          </td>
          <td>
            <Select
              inline
              value={conciliacao}
              onChange={(e) => setConciliacao(e.target.value)}
              aria-label="Conciliação"
            >
              {STATUS.map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </Select>
          </td>
          <td>{faturaCelula}</td>
          <td>
            <Button size="sm" variant="secondary" onClick={salvar}>
              {label}
            </Button>
          </td>
        </>
      ) : (
        <>
          <td>{lanc.categoria ?? '(sem categoria)'}</td>
          <td>{statusLabel}</td>
          <td>{faturaCelula}</td>
        </>
      )}
    </tr>
  );
}
